import { useMemo, useRef, type ReactNode } from 'react';
import { Link, Navigate, Outlet } from '@tanstack/react-router';

import { cn } from '@/lib/utils';
import { useWorkspace } from '@/features/workspaces';
import { PageSectionContext } from '@/shared/components/pageSection';
import { useOverflowing } from '@/shared/hooks/useOverflowing';
import { useNavViewer } from '../hooks/useNavViewer';
import { visibleNavItems, type NavItem, type TabBadge } from '../navigation';

export interface SectionLayoutProps {
  /** The sidebar entry's name: Integrations, AI usage, Settings. */
  title: string;
  tabs: NavItem[];
  workspaceId: string;
  /** One description for every tab; see `PageSection`. */
  description?: string;
  /** Page controls under the tabs; see `PageSection`. */
  toolbar?: boolean;
  /** The band on every tab (spec 227); see `PageSection`. */
  surface?: 'page' | 'inverse';
  /**
   * Drawn in place of the tabs' outlet: what the group shows while its own read is
   * pending or has failed (Settings). Absent, the open tab's route renders.
   */
  children?: ReactNode;
}

/**
 * A sidebar entry that groups pages as tabs (spec 224).
 *
 * Each tab is still its own page at its own address; this only hands every page
 * under it the section's name and the strip, which its own `PageHeader` draws.
 */
export function SectionLayout({
  title,
  tabs,
  workspaceId,
  description,
  toolbar,
  surface,
  children,
}: SectionLayoutProps) {
  const viewer = useNavViewer(workspaceId);
  const shown = visibleNavItems(tabs, viewer);
  // What the strip holds, as one value: `shown` is a fresh array each render, and a
  // tab's figures change with the reads behind them.
  const shownKey = shown
    .map((tab) => `${tab.to}:${badgeKey(tab.tabBadge)}`)
    .join();
  const section = useMemo(
    () => ({
      title,
      description,
      toolbar,
      surface,
      tabs: (
        <SectionTabs title={title} tabs={shown} workspaceId={workspaceId} />
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `shownKey` stands for `shown`
    [title, workspaceId, description, toolbar, surface, shownKey],
  );
  return (
    <PageSectionContext.Provider value={section}>
      {children ?? <Outlet />}
    </PageSectionContext.Provider>
  );
}

function badgeKey(badge: TabBadge | undefined): string {
  if (!badge) return '';
  return `${String(badge.count ?? '')}/${String(badge.attention?.count ?? '')}`;
}

/**
 * The strip: links, not a tablist. Each tab is an address, so it is navigation —
 * the current one says so with `aria-current`, and the browser's own link keys
 * (open in a new tab, copy the address) work on every one. Drawn as the app's tab
 * underline so it reads as the Settings strip does.
 *
 * It fades at its right edge while it runs on past it (spec 227 FR-011): on a
 * phone a fifth tab fell past the edge, cut mid-word, with nothing to say it
 * could be scrolled to.
 */
export function SectionTabs({ title, tabs, workspaceId }: SectionLayoutProps) {
  const strip = useRef<HTMLUListElement>(null);
  // Two or three tabs share a phone's width; more scroll with the fade
  // (docs/phone.md PH-03, spec 277).
  const split = tabs.length <= 3;
  useOverflowing(strip);
  return (
    <nav aria-label={title} className="border-border shrink-0 border-b">
      <ul
        ref={strip}
        className={cn(
          'overflow-fade flex items-end gap-4 overflow-x-auto overflow-y-hidden',
          split && 'max-md:gap-0',
        )}
      >
        {tabs.map((tab) => (
          <li key={tab.to} className={cn('shrink-0', split && 'max-md:flex-1')}>
            <Link
              to={tab.to}
              params={{ workspaceId }}
              className={cn(
                '-mb-px inline-block border-b-2 px-0.5 pt-1 pb-1.5 text-sm whitespace-nowrap transition-colors pointer-coarse:inline-flex pointer-coarse:min-h-11 pointer-coarse:items-center',
                'focus-visible:ring-ring focus-visible:rounded-sm focus-visible:ring-3 focus-visible:outline-none',
                split && 'max-md:flex max-md:w-full max-md:justify-center',
              )}
              activeProps={{
                className: 'border-accent-line text-foreground font-medium',
              }}
              inactiveProps={{
                className:
                  'text-muted-foreground hover:text-foreground hover:border-border border-transparent',
              }}
            >
              {tab.label}
              <TabBadgeMark badge={tab.tabBadge} />
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
}

/**
 * How many are in the tab, then who waits for an answer, which is the one that
 * asks for something (spec 227 FR-010; the People tab's markup since spec 194).
 */
function TabBadgeMark({ badge }: { badge: TabBadge | undefined }) {
  if (!badge) return null;
  return (
    <>
      {badge.count !== undefined ? (
        <span className="text-muted-foreground ml-1.5 text-xs tabular-nums">
          {badge.count}
        </span>
      ) : null}
      {badge.attention && badge.attention.count > 0 ? (
        <span className="bg-primary text-primary-foreground ml-1.5 rounded-full px-1.5 text-2xs font-medium tabular-nums">
          {badge.attention.count}
          <span className="sr-only"> {badge.attention.label}</span>
        </span>
      ) : null}
    </>
  );
}

/**
 * The entry's own address opens its first tab this reader may see. It waits for the
 * workspace read: a reader sent to a members-only tab on a guess would land on a
 * refusal, then be moved.
 */
export function SectionIndex({
  tabs,
  workspaceId,
}: Omit<SectionLayoutProps, 'title'>) {
  const { isPending } = useWorkspace(workspaceId);
  const viewer = useNavViewer(workspaceId);
  const first = visibleNavItems(tabs, viewer).at(0);
  if (isPending || !first) return null;
  return <Navigate to={first.to} params={{ workspaceId }} replace />;
}
